import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../Icon';
import ghostIcon from '../../assets/images/brand/ghost_icon.svg';
import { supabase } from '../../supabaseClient';
import { getAvatarInitials, getAvatarColorHex } from '../../helpers/stringUtils';
import ModalSettings from '../Overlays/ModalSettings';

export default function Nav({ activePanel, setActivePanel, user, activeTabId, tabs, hideButtons = [], hideSettings = [], navMode = 'full', showShareButton = false, onShareClick }) {
  // Button sets per navMode
  const allNavButtons = {
    full: [
      { id: 'projectButton', icon: 'tree-view', label: 'Projects' },
      { id: 'chatsButton', icon: 'chat-ghost', label: 'Chats' },
      { id: 'versionsDivider', divider: true },
      // { id: 'outlineButton', icon: 'ph-file-text', label: 'Outline' },
      // { id: 'shareButton', icon: 'ph-share-network', label: 'Share', action: 'share' },
      { id: 'versionsButton', icon: 'clock-counter-clockwise', label: 'Versions' }
    ],
    viewer: [
      { id: 'projectButton', icon: 'rocket-launch', label: 'Projects' }
    ],
    doc: [
      { id: 'printButton', icon: 'printer', label: 'Print', action: 'print' },
      { id: 'downloadButton', icon: 'download-simple', label: 'Download', action: 'download', disabled: true }
    ],
    admin: [
      { id: 'database', icon: 'database', label: 'Database', to: '/admin/database' },
      { id: 'ai', icon: 'robot', label: 'AI', to: '/admin/ai' },
      { id: 'users', icon: 'users', label: 'Users', to: '/admin/users' },
      { id: 'storage', icon: 'hard-drives', label: 'Storage', to: '/admin/storage' }
    ]
  };

  const buttons = (allNavButtons[navMode] || allNavButtons.full)
    .filter(btn => !hideButtons.includes(btn.id))
    .filter(btn => showShareButton || btn.id !== 'shareButton');

  const [settingsOpen, setSettingsOpen] = useState(false);

  const handleClick = (button) => {
    if (button.to) {
      navigate(button.to);
      return;
    }

    if (button.action === 'print') {
      window.print();
      return;
    }
    if (button.action === 'download') {
      // placeholder – wired up later
      return;
    }
    if (button.action === 'share') {
      onShareClick?.();
      return;
    }
    // Toggle: if clicking the active button, close the panel
    setActivePanel(prev => prev === button.id ? null : button.id);
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      // User state will be cleared automatically by onAuthStateChange in App.js
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  const navigate = useNavigate();

  return (
    <div id="nav">
      <div id="logo">
        <button
          type="button"
          className="logoButton"
          onClick={() => navigate('/')}
          aria-label="Home"
        >
          <img alt="ghost" src={ghostIcon} />
        </button>
      </div>
      {buttons.map((btn) => (
        btn.divider ? (
          <div key={btn.id} className="navDivider" aria-hidden="true" />
        ) : (
        <button
          id={btn.id}
          key={btn.id}
          onClick={() => handleClick(btn)}
          className={`iconButton ${activePanel === btn.id ? 'active' : ''}`}
          aria-label={btn.label}
          data-tooltip={btn.label}
          data-tooltip-placement="right"
          disabled={btn.disabled || false}
        >
          <Icon icon={btn.icon} className={`icon ${btn.icon}`} />
        </button>
        )
      ))}
      {user && (
        <button
          id="signOutButton"
          onClick={handleSignOut}
          className="iconButton"
          style={{ marginTop: 'auto' }}
          aria-label="Sign Out"
          data-tooltip="Sign Out"
          data-tooltip-placement="right"
        >
          <Icon icon={'sign-out'} className="icon sign-out" />
        </button>
      )}
      <button
        id="settingsButton"
        onClick={() => setSettingsOpen(prev => !prev)}
        className="iconButton"
        style={{ marginTop: user ? '0' : 'auto' }}
        aria-label="Settings"
        data-tooltip="Settings"
        data-tooltip-placement="right"
      >
        <Icon icon={'gear'} className="icon gear" />
      </button>
      <ModalSettings
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        user={user}
        activeTabId={activeTabId}
        tabs={tabs}
        hideSettings={hideSettings}
      />

      {user && (
        <div
          className="navAvatar"
          style={{ backgroundColor: getAvatarColorHex(user.id || user.email) }}
          aria-label="User avatar"
          data-tooltip={user.email || 'User'}
          data-tooltip-placement="top right"
        >
          {getAvatarInitials(user.email)}
        </div>
      )}
    </div>
  );
}
